<template>
    <div>
      <SettingHeaderTab />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Customer List</h5>
          <p class="page-sub">Manage all customers across your business</p>
        </div>
  
        <div class="page-actions">
  
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input
              type="text"
              class="search-input"
              placeholder="Search customers..."
              v-model="query"
            />
          </div>
  
          <!-- Export -->
          <div class="dropdown" ref="exportDrop">
            <button class="btn-outline" @click="exportOpen = !exportOpen">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu" :class="{ show: exportOpen }">
              <li>
                <a class="dropdown-item" href="#" @click.prevent="openExportModal('pdf')">
                  <font-awesome-icon icon="file-pdf" class="text-danger me-2" /> PDF
                </a>
              </li>
              <li>
                <a class="dropdown-item" href="#" @click.prevent="openExportModal('csv')">
                  <font-awesome-icon icon="file-csv" class="text-success me-2" /> CSV
                </a>
              </li>
            </ul>
          </div>
  
          <!-- Bulk Upload -->
          <router-link
            to="bulk-customers-upload"
            class="btn-outline"
            :class="{ 'btn--disabled': permission.create_customers != 1 }"
          >
            <font-awesome-icon icon="upload" /> Bulk Upload
          </router-link>
  
          <!-- Create -->
          <router-link
            to="create-customer"
            class="btn-primary-action"
            :class="{ 'btn--disabled': permission.create_customers != 1 }"
          >
            <font-awesome-icon icon="plus" /> Create Customer
          </router-link>
  
          <!-- Bulk Delete -->
          <button
            class="btn-danger-outline"
            :disabled="!canBulkDelete"
            data-bs-toggle="modal"
            data-bs-target="#bulkModal"
          >
            <font-awesome-icon icon="trash" />
            Bulk Delete
            <span v-if="selectedModel.length > 0" class="bulk-count">{{ selectedModel.length }}</span>
          </button>
  
        </div>
      </div>
  
      <!-- Data card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading customers...</p>
        </div>
  
        <!-- 403 -->
        <div v-else-if="permission_status === 403" class="state-block">
          <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to view customers.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--blue"><font-awesome-icon icon="users" /></div>
          <p class="state-title">No customers found</p>
          <p class="state-sub">
            {{ query ? 'No results match your search.' : 'Create your first customer to get started.' }}
          </p>
          <router-link
            v-if="!query && permission.create_customers == 1"
            to="create-customer"
            class="btn-primary-action mt-3"
          >
            <font-awesome-icon icon="plus" /> Create Customer
          </router-link>
        </div>
  
        <template v-else>
  
          <!-- Toolbar -->
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong>
              of <strong>{{ pagination.total }}</strong>
              <span v-if="selectedModel.length > 0" class="selected-hint">· {{ selectedModel.length }} selected</span>
            </span>
            <div class="toolbar-right">
              <label class="table-count">Rows:</label>
              <select v-model.number="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
              </select>
            </div>
          </div>
  
          <!-- Table -->
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="col-check">
                    <input type="checkbox" class="row-check" v-model="allSelected" @change="selectAll" />
                  </th>
                  <th>#</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(customer, index) in details"
                  :key="customer.id"
                  :class="{ 'row--selected': selectedModel.includes(customer.id) }"
                >
                  <td class="col-check">
                    <input type="checkbox" class="row-check" v-model="selectedModel" :value="customer.id" />
                  </td>
  
                  <td class="text-muted-cell">{{ pagination.from + index }}</td>
  
                  <td>
                    <div class="customer-cell">
                      <div class="customer-avatar">{{ initials(customer.customer_name) }}</div>
                      <div>
                        <div class="customer-name">{{ customer.customer_name }}</div>
                        <div class="customer-email">{{ customer.email }}</div>
                      </div>
                    </div>
                  </td>
  
                  <td class="text-muted-cell">{{ customer.phone_no }}</td>
  
                  <td>
                    <span :class="['status-badge', customer.is_active ? 'status-badge--active' : 'status-badge--inactive']">
                      {{ customer.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>

                  <td class="col-actions">
                    <div class="actions-cell">
                      <router-link
                        :to="permission.edit_customers == 1 ? { name: 'Edit Customer', params: { companyCode: $route.params.companyCode, id: customer.id } } : '#'"
                        class="action-btn action-btn--edit"
                        :class="{ 'action-btn--disabled': permission.edit_customers != 1 }"
                        title="Edit"
                      >
                        <font-awesome-icon icon="pen" />
                      </router-link>
                      <button
                        class="action-btn action-btn--toggle"
                        :disabled="permission.edit_customers != 1 || togglingId === customer.id"
                        :class="{ 'action-btn--disabled': permission.edit_customers != 1 }"
                        :title="customer.is_active ? 'Set Inactive' : 'Set Active'"
                        @click="toggleStatus(customer)"
                      >
                        <font-awesome-icon v-if="togglingId === customer.id" icon="spinner" spin />
                        <font-awesome-icon v-else :icon="customer.is_active ? 'toggle-on' : 'toggle-off'" />
                      </button>
                      <button
                        class="action-btn action-btn--delete"
                        :disabled="permission.delete_customers != 1"
                        :class="{ 'action-btn--disabled': permission.delete_customers != 1 }"
                        title="Delete"
                        @click="openDelete(customer)"
                        data-bs-toggle="modal"
                        data-bs-target="#singleModal"
                      >
                        <font-awesome-icon icon="trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
  
          <!-- Pagination -->
          <div v-if="pagination.lastPage > 1" class="pagination-bar">
            <span class="pagination-info">Showing {{ pagination.from }}–{{ pagination.to }} of {{ pagination.total }}</span>
            <div class="pagination-controls">
              <button class="pg-btn" :disabled="pagination.currentPage <= 1" @click="tableData.page = 1; fetchData()">«</button>
              <button class="pg-btn" :disabled="pagination.currentPage <= 1" @click="tableData.page = pagination.currentPage - 1; fetchData()">‹ Prev</button>
              <span class="pg-pages">
                <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === pagination.currentPage }" @click="tableData.page = p; fetchData()">{{ p }}</button>
              </span>
              <button class="pg-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="tableData.page = pagination.currentPage + 1; fetchData()">Next ›</button>
              <button class="pg-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="tableData.page = pagination.lastPage; fetchData()">»</button>
            </div>
          </div>
  
        </template>
      </div>
  
      <!-- Delete modals -->
      <SingleDeleteModal
        :modalId="'singleModal'"
        @deleteSingle="callSingleCustomerDeleteButton"
        :modalText="'Are you sure you want to delete this customer?'"
        :action="'deleteSingle'"
      />
      <BulkDeleteModal
        :modalId="'bulkModal'"
        @deleteBulk="callBulkCustomerDeleteButton"
        :modalText="'Are you sure you want to delete the selected customers?'"
      />
  
    </div>

    <!-- ── Export Column Picker ──────────────────────────────────────────── -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false">
              <font-awesome-icon icon="times" />
            </button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-ghost-cp" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary-cp"
              :disabled="selectedExportCols.length === 0 || exportLoading"
              @click="doExport()">
              <font-awesome-icon
                :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'"
                :spin="exportLoading" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </template>

  <script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useStore } from 'vuex';
  import { reusableMixin } from '../../../lib/reusableMixin';
  import SettingHeaderTab from './components/settingheadertab.vue';
  import SingleDeleteModal from '../../../components/ActionModal.vue';
  import BulkDeleteModal   from '../../../components/bulkDeleteModal.vue';
  import jsPDF from 'jspdf';
  import 'jspdf-autotable';
  
  export default {
    name: 'CustomerList',
  
    components: { SettingHeaderTab, SingleDeleteModal, BulkDeleteModal },
  
    mixins: [reusableMixin],
  
    data() {
      return {
        print_content:     [],
        headerArrayColumn: ['S/N', 'Customer Name', 'Email', 'Phone Number', 'Address', 'City', 'State', 'Country'],
  
        details:          [],
        loading:          false,
        permission_status: null,
        query:            null,
        customer_id:      null,
        exportOpen:       false,
        exportLoading:    false,
        showExportModal:  false,
        pendingExportFmt: 'csv',
        selectedExportCols: [],
        exportColumnDefs: [
          { key: 'customer_name', label: 'Customer Name', pdfWidth: 150 },
          { key: 'email',         label: 'Email',         pdfWidth: 190 },
          { key: 'phone',         label: 'Phone',         pdfWidth: 90 },
          { key: 'address',       label: 'Address',       pdfWidth: 160 },
          { key: 'city',          label: 'City',          pdfWidth: 66 },
          { key: 'state',         label: 'State',         pdfWidth: 66 },
          { key: 'status',        label: 'Status',        pdfWidth: 39 },
        ],
  
        selectedModel: [],
        allSelected:   false,
        togglingId:    null,
  
        tableData: { page: 1,
          draw:   0,
          length: 5,
          search: '',
          column: 0,
          dir:    'desc',
          status: [0, 1, 2, 3],
          query:  '',
        },
  
        pagination: {
          lastPage: '', currentPage: '', total: '',
          lastPageUrl: '', nextPageUrl: '', prevPageUrl: '',
          from: 1, to: '',
        },
  
        toast:  useToast(),
        store:  useStore(),
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
      canBulkDelete() {
        return this.permission.delete_customers == 1 && this.selectedModel.length > 0;
      },
      pageRange() {
        if (!this.pagination.lastPage) return [];
        const cur = this.pagination.currentPage, last = this.pagination.lastPage;
        const range = [];
        for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
        return range;
      },
    },
  
    methods: {
      // ── Helpers ─────────────────────────────────────────────────────────
      initials(name) {
        if (!name) return '?';
        return name.split(' ').map(p => p[0]).join('').substring(0, 2).toUpperCase();
      },
  
      // ── Export ──────────────────────────────────────────────────────────
      openExportModal(format) {
        this.pendingExportFmt   = format;
        this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
        this.showExportModal    = true;
        this.exportOpen         = false;
      },

      getRowValue(row, key) {
        switch (key) {
          case 'customer_name': return row.customer_name || '—';
          case 'email':         return row.email || '—';
          case 'phone':         return row.phone_no || '—';
          case 'address':       return row.address || '—';
          case 'city':          return row.city?.name || '—';
          case 'state':         return row.state?.name || '—';
          case 'status':        return row.is_active ? 'Active' : 'Inactive';
          default:              return '—';
        }
      },

      async doExport() {
        if (!this.selectedExportCols.length) return;
        this.exportLoading   = true;
        this.showExportModal = false;
        try {
          const params = { ...this.tableData, page: 1, length: 10000 };
          const res    = await axios.get('/settings/customers', { params });
          const rows   = Array.isArray(res.data?.data?.customers?.data) ? res.data.data.customers.data : [];
          const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
          const headers = cols.map(c => c.label);
          const body    = rows.map(row => cols.map(c => this.getRowValue(row, c.key)));
          const date    = new Date().toISOString().slice(0, 10);
          if (this.pendingExportFmt === 'csv') {
            const csv  = [headers, ...body]
              .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
              .join('\n');
            const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url; a.download = `customers-${date}.csv`; a.click();
            URL.revokeObjectURL(url);
          } else {
            const doc       = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
            const usable    = 761;
            const totalW    = cols.reduce((s, c) => s + c.pdfWidth, 0);
            const scale     = usable / totalW;
            const colStyles = {};
            cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
            doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 43, 69);
            doc.text('Customer List', 40, 46);
            doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(122, 144, 176);
            doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);
            doc.autoTable({
              startY: 78, head: [headers], body,
              styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 43, 69], overflow: 'ellipsize' },
              headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
              alternateRowStyles: { fillColor: [248, 250, 252] },
              columnStyles: colStyles, tableWidth: usable, margin: { left: 40, right: 40 },
              didDrawPage(data) {
                doc.setFontSize(8); doc.setTextColor(192, 202, 214);
                doc.text(`Page ${data.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 16, { align: 'center' });
              },
            });
            doc.save(`customers-${date}.pdf`);
          }
        } catch { this.toast.error('Export failed. Please try again.'); }
        finally { this.exportLoading = false; }
      },
  
      // ── Selection ────────────────────────────────────────────────────────
      selectAll() {
        this.selectedModel = this.allSelected ? this.details.map(c => c.id) : [];
      },
  
      openDelete(customer) {
        this.customer_id = customer.id;
      },
  
      // ── Data ────────────────────────────────────────────────────────────
      dataToPrint(details) {
        this.print_content = details.map((c, idx) => ({
          sn:            idx + 1,
          customer_name: c.customer_name ?? '',
          email:         c.email ?? '',
          phone_number:  c.phone_no ?? '',
          address:       c.address ?? '',
          city:          c.city?.name ?? '',
          state:         c.state?.name ?? '',
          country:       c.country?.name ?? '',
        }));
      },
  
      fetchData(url = '/settings/customers') {
        this.loading = true;
        this.tableData.query = this.query;
        this.tableData.draw++;
  
        axios.get(url, { params: this.tableData })
          .then(({ data }) => {
            this.details = data.data.customers.data;
            this.dataToPrint(data.data.customers2);
            this.configPagination(data.data.customers);
          })
          .catch(err => {
            if (err?.response?.status === 403) {
              this.permission_status = 403;
              this.toast.error(err.response.data.message);
            }
          })
          .finally(() => { this.loading = false; });
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
  
      callSingleCustomerDeleteButton() {
        axios.delete(`/settings/customer/${this.customer_id}`)
          .then(() => {
            this.toast.success('Customer deleted successfully.');
            this.fetchData();
          })
          .catch(err => {
            const status = err?.response?.status;
            if (status === 403) this.toast.error(err.response.data.message, { timeout: 10000 });
            else this.toast.error('Something went wrong. Please try again.');
          });
      },
  
      toggleStatus(customer) {
        this.togglingId = customer.id;
        axios.put(`/settings/customer/${customer.id}`, {
          customer_name: customer.customer_name,
          phone_no:      customer.phone_no,
          email:         customer.email,
          is_active:     !customer.is_active,
        })
          .then(() => {
            customer.is_active = !customer.is_active;
            this.toast.success(`Customer ${customer.is_active ? 'activated' : 'deactivated'} successfully.`);
          })
          .catch(err => {
            this.toast.error(err.response?.data?.message ?? 'Failed to update status.');
          })
          .finally(() => { this.togglingId = null; });
      },

      callBulkCustomerDeleteButton() {
        axios.post('/settings/customer/delete-bulk', { customers: this.selectedModel })
          .then(({ data }) => {
            this.toast.success(data.message);
            this.selectedModel = [];
            this.allSelected   = false;
            this.fetchData();
          })
          .catch(err => {
            const status = err.response?.status;
            if (status === 422) this.toast.error(err.response.data.message[0]);
            else this.toast.error(err.response?.data?.message ?? 'Bulk delete failed.');
          });
      },
    },
  
    watch: {
      query() { this.fetchData(); },
    },
  
    created() {
      this.fetchData();
      // close export dropdown on outside click
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.dropdown')) this.exportOpen = false;
      });
    },
  };
  </script>
  
  <style scoped>
  /* ── Page ────────────────────────────────────────────────────────────── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Search ──────────────────────────────────────────────────────────── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 33px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:210px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  
  /* ── Buttons ─────────────────────────────────────────────────────────── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; text-decoration:none; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover:not(.btn--disabled) { background:#003b8e; color:#fff; text-decoration:none; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; text-decoration:none; transition:background .15s; white-space:nowrap; }
  .btn-outline:hover:not(.btn--disabled) { background:#f4f7fb; color:#1a2b45; text-decoration:none; }
  .btn-danger-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#e03535; border:1px solid #fecaca; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; cursor:pointer; transition:all .15s; white-space:nowrap; }
  .btn-danger-outline:hover:not(:disabled) { background:#fff5f5; }
  .btn-danger-outline:disabled { opacity:.45; cursor:not-allowed; }
  .btn--disabled { opacity:.45; pointer-events:none; }
  .bulk-count { display:inline-flex; align-items:center; justify-content:center; background:#e03535; color:#fff; border-radius:10px; font-size:11px; font-weight:700; padding:1px 6px; }
  .mt-3 { margin-top:12px; }
  .me-2 { margin-right:8px; }
  
  /* ── Export dropdown ─────────────────────────────────────────────────── */
  .dropdown      { position:relative; }
  .dropdown-menu { position:absolute; top:calc(100% + 6px); right:0; background:#fff; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); padding:6px; min-width:160px; z-index:200; display:none; }
  .dropdown-menu.show { display:block; }
  .dropdown-item { display:flex; align-items:center; gap:8px; padding:8px 12px; font-size:13px; border-radius:7px; color:#1a2b45; cursor:pointer; text-decoration:none; transition:background .12s; }
  .dropdown-item:hover { background:#f4f7fb; }
  
  /* ── Data card ───────────────────────────────────────────────────────── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ──────────────────────────────────────────────────────────── */
  .state-block      { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon       { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.08); color:#004AAD; }
  .state-icon--red  { background:#fff0f0; color:#e03535; }
  .state-title      { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub        { font-size:13px; color:#7a90b0; margin:0; }
  .text-muted-cell  { color:#4a5e78; }
  
  /* ── Table toolbar ───────────────────────────────────────────────────── */
  .table-toolbar  { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count    { font-size:13px; color:#7a90b0; }
  .table-count strong { color:#1a2b45; }
  .selected-hint  { color:#004AAD; font-weight:600; }
  .toolbar-right  { display:flex; align-items:center; gap:8px; }
  .rows-select    { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  /* ── Table ───────────────────────────────────────────────────────────── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody tr.row--selected { background:#edf4ff; }
  .data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
  .col-check   { width:44px; padding-left:20px !important; }
  .col-actions { width:90px; text-align:center; }
  .row-check   { width:15px; height:15px; cursor:pointer; accent-color:#004AAD; }
  
  /* ── Customer cell ───────────────────────────────────────────────────── */
  .customer-cell   { display:flex; align-items:center; gap:10px; }
  .customer-avatar { width:32px; height:32px; border-radius:50%; background:rgba(0,74,173,.1); color:#004AAD; font-size:11px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .customer-name   { font-size:13px; font-weight:600; color:#1a2b45; }
  .customer-email  { font-size:12px; color:#7a90b0; margin-top:1px; }
  
  /* ── Action buttons ──────────────────────────────────────────────────── */
  .actions-cell       { display:flex; align-items:center; justify-content:center; gap:6px; }
  .action-btn         { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; color:#7a90b0; cursor:pointer; transition:all .15s; text-decoration:none; }
  .action-btn--edit:hover:not(.action-btn--disabled)   { background:#e8f4fd; color:#004AAD; border-color:#c5d6f5; }
  .action-btn--toggle:hover:not(:disabled):not(.action-btn--disabled) { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .action-btn--delete:hover:not(:disabled)             { background:#fff0f0; color:#e03535; border-color:#f5c6c6; }
  .action-btn--disabled { opacity:.35; pointer-events:none; }

  /* ── Status badge ────────────────────────────────────────────────────── */
  .status-badge          { display:inline-flex; align-items:center; padding:2px 9px; border-radius:20px; font-size:11.5px; font-weight:600; white-space:nowrap; }
  .status-badge--active  { background:#f0fdf4; color:#16a34a; }
  .status-badge--inactive{ background:#f8fafc; color:#7a90b0; }
  
  /* ── Table footer ────────────────────────────────────────────────────── */
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  .pagination-bar      { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-top:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .pagination-info     { font-size:13px; color:#7a90b0; }
  .pagination-controls { display:flex; align-items:center; gap:4px; }
  .pg-btn  { padding:6px 11px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; }
  .pg-btn:hover:not(:disabled) { background:#f4f7fb; border-color:#c8d6e8; }
  .pg-btn:disabled { opacity:.4; cursor:not-allowed; }
  .pg-pages { display:flex; gap:3px; }
  .pg-num  { min-width:34px; padding:6px 8px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; text-align:center; }
  .pg-num:hover { background:#f4f7fb; border-color:#c8d6e8; }
  .pg-num--active { background:#004AAD; border-color:#004AAD; color:#fff; font-weight:600; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:768px) {
    .page-header, .page-actions { flex-direction:column; align-items:stretch; }
    .search-input { width:100%; }
  }

  /* ── Column picker modal ──────────────────────────────────────────────── */
  .col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
  .col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
  .col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
  .col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
  .col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
  .col-picker-close:hover { color:#1a2b45; }
  .col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
  .col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
  .col-picker-link:hover { text-decoration:underline; }
  .col-picker-sep      { color:#c5d3e0; }
  .col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
  .col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
  .col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
  .col-picker-item:hover { background:#f4f7fb; }
  .col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
  .col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
  .col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
  .btn-ghost-cp   { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:1px solid #e4eaf2; cursor:pointer; font-family:inherit; }
  .btn-ghost-cp:hover { background:#e4eaf2; }
  .btn-primary-cp { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
  .btn-primary-cp:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-cp:disabled { opacity:.45; cursor:not-allowed; }
  .fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
  .fade-enter, .fade-leave-to { opacity:0; }
  </style>